import React from "react";
import { Page, Text, View, Document, StyleSheet, Image, Link } from "@react-pdf/renderer";
import { DQC_COLORS, KSAB_COLOR_MAPPING } from "../../../../../utils/constants";
import { formatDuration } from "../../../../../utils/utils";
import { TARGET_AGE_MAP } from "../../../../../utils/META/cat_109";
import { Svg, Circle, Text as SvgText } from "@react-pdf/renderer";
import PoweredByDQ from '../../../../../assets/images/PoweredByDQ_Default.png';

const CircleLetterIcon = ({ letter }) => (
    <Svg width="9" height="9" viewBox="0 0 16 16">
        <Circle cx={8} cy={8} r={7} stroke="#444" strokeWidth={1} fill="none" />
        <SvgText x={8} y={11} fontSize={9} textAnchor="middle" fill="#444">{letter}</SvgText>
    </Svg>
);

const styles = StyleSheet.create({
    page: { paddingTop: 100, paddingBottom: 72, paddingLeft: 54, paddingRight: 70, fontSize: 10, fontFamily: "Helvetica", lineHeight: 1.5 },

    // Header / Footer
    header: {
        position: "absolute", top: 60, left: 32, right: 36, height:65,
        flexDirection: "row", justifyContent: "flex-end", alignItems: "center",
    },
    headerLogo: { height: '45px', marginLeft: 6, marginTop: 16 , marginBottom: '80px',},

    footer: {
        position: "absolute", bottom: 50, left: 32, right: 32,
        flexDirection: "row", justifyContent: "center", alignItems: "center",
    },
    footerRow: { flexDirection: "row", alignItems: "center", justifyContent: "center", flexWrap: "wrap" },
    footerCopy: { fontSize: 8, color: "#444" },
    footerSpacer: { width: 4 },
    footerPage: { fontSize: 8, color: "#444" },

    // Title block
    subTitleTop: { fontSize: 13, textAlign: "center",marginTop: '30px', marginBottom: 2, },
    title: { fontSize: 18, marginTop: '4px', marginBottom: '4px', fontFamily: "Helvetica-Bold", textAlign: "center" },
    subtitle: { fontSize: 13, textAlign: "center", marginTop: 2, fontStyle: "italic" },

    metaRow: { marginTop: 10, marginBottom: 8 },
    metaLine: { fontSize: 12, textAlign: "left", marginBottom: 2 },
    h2: { fontSize: 14, fontFamily: "Helvetica-Bold", marginTop: '12px', marginBottom: 4 },
    h3: { fontSize: 13, fontFamily: "Helvetica-Bold", marginTop: 6, marginBottom: 3 },

    durationModule:  { fontSize: 9, color: "#000" },
    durationUnit:    { fontSize: 9, color: "#000" },
    durationSession: { fontSize: 9, color: "#000" },

    divider: { height: 1, backgroundColor: "#000", marginVertical: 8 },
    moduleDivider: {
        height: 0.8,
        backgroundColor: "#000",
        width: "100%",
        alignSelf: "center",
        marginVertical: 10,
        borderRadius: 1,
    },
    unitDivider: {
        height: 0.6,
        backgroundColor: "#d9d9d9",
        width: "77%",
        alignSelf: "center",    
        marginVertical: 12,
        borderRadius: 1,
    },

    // KSAB
    ksabRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", marginTop: '13px', marginBottom: '13px' },
    ksabDot: { width: 9, height: 9, borderRadius: 5, marginRight: 4 },
    ksabLabel: { fontSize: 9, fontWeight: "bold", marginRight: 10 },

    ksabPill: {
        alignSelf: "center",
        backgroundColor: "#f3f3f3",
        borderRadius: 12,
        paddingVertical: 18,
        paddingHorizontal: 13,
        marginTop: '13px',
        marginBottom: '15px',

        // layout
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    ksabItem: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 14,   // spacing between items
    },
    ksabDotLg: {        // slightly larger than before for visibility
        width: 11,
        height: 11,
        borderRadius: 5,
        marginRight: 6,
    },
    ksabLabelBold: { fontSize: 11, fontFamily: "Helvetica-Bold", marginRight: 10 },

    // DQ code chips
    tagWrap: { flexDirection: "row", flexWrap: "wrap", marginTop: 4, marginBottom: 4 },
    tag: {
        backgroundColor: "#e6f1fe",
        borderRadius: 14,
        paddingHorizontal: 8,
        paddingVertical: 5,
        marginRight: 6,
        marginBottom: 6,
        maxWidth: 220,
    },
    tagText: { fontSize: 6, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },

    // Hierarchy / indentation
    moduleBlock: { marginBottom: 12 },
    unitBlock: {
        marginTop: 8,
        marginLeft: 10,              // first-level indent (Unit)
        paddingLeft: 8,
        borderLeftWidth: 0,
    },
    unitTitleBand: {
        borderRadius: 4,
        paddingVertical: 4,
        paddingHorizontal: 6,
        alignSelf: "flex-start",
        marginBottom: 4,
    },
    unitTitleText: { fontSize: 14, paddingHorizontal: 6, fontFamily: "Helvetica-Bold" },

    sessionBlock: {
        marginTop: '14px',
        marginBottom: '10px',
        marginLeft: '26px',             // deeper indent (Session)
        paddingLeft: '18px',
        paddingRight: '18px',
        paddingTop: '8px',
        paddingBottom: '8px',
        borderLeftWidth: 0,
        borderLeftColor: "#e0d6a9",
    },
    sessionHeader: { fontSize: 14, fontFamily: "Helvetica-Bold", marginBottom: 6 },
    sessionMeta: { marginTop: 2, marginBottom: 3,fontSize:'12px' },
    bullet: { fontSize: 12, marginLeft: '10px', marginBottom: 3 },
    
    // Table styles
    table: { 
        marginTop: 8, 
        marginBottom: 8, 
        borderWidth: 1, 
        borderColor: "#000",
        width: "100%"
    },
    tableRow: { 
        flexDirection: "row", 
        borderBottomWidth: 1, 
        borderColor: "#000",
        minHeight: 30
    },
    tableHeader: { 
        flex: 1, 
        padding: 6, 
        backgroundColor: "#f0f0f0", 
        borderRightWidth: 1, 
        borderColor: "#000",
        justifyContent: "center"
    },
    tableHeaderText: { fontSize: 11, fontFamily: "Helvetica-Bold" },
    tableCell: { 
        flex: 1, 
        padding: 6, 
        borderRightWidth: 1, 
        borderColor: "#000",
        justifyContent: "center"
    },
    tableCellText: { fontSize: 10 },
    
    // Teacher notes
    teacherNotes: { marginTop: 10, padding: 8, backgroundColor: "#f9f9f9", borderRadius: 4 },
    teacherNotesTitle: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 4 },
    teacherNotesText: { fontSize: 11, marginBottom: 3 },

    legend: { marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: "#cccccc" },
    legendTitle: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 8 },

    // Frameworks Aligned page styles
    frameworksPage: {
        paddingTop: 100,
        paddingBottom: 72,
        paddingLeft: 54,
        paddingRight: 70,
        fontSize: 10,
        fontFamily: "Helvetica",
        lineHeight: 1.5
    },
    frameworksList: {
        marginTop: 10,
        paddingLeft: 20
    },
    frameworkItem: {
        flexDirection: "row",
        marginBottom: 4,
        paddingLeft: 10
    },
    frameworkBullet: {
        fontSize: 20,
        marginRight: 10,
        fontWeight: "bold"
    },
    frameworkLink: {
        fontSize:'12px',
        color: "#0066cc",
        textDecoration: "underline",
        fontFamily: "Helvetica",
    },
    frameworkText: {
        fontSize: 12,
        color: "#000"
    },
    noFrameworks: {
        fontSize: 12,
        color: "#666",
        fontStyle: "italic",
        textAlign: "center",
        marginTop: 20
    },
});

const TeachingPlanPdfDoc = ({ programData, programAudience, programDuration, modules = [], isEducatorView, selectedLanguage = 'en-US', }) => {
    const otherLangKey = Object.keys(programData?.translations || {}).find(lang => lang !== 'en-US');
    const otherTranslation = otherLangKey ? programData?.translations?.[otherLangKey] : null;
    const programName = (otherTranslation?.full_name || otherTranslation?.name || '').trim() || programData?.full_name;
    const programDescription = (otherTranslation?.description || '').trim() || programData?.description;
    const totalModules = modules.length;
    const totalUnits = modules.reduce((acc, m) => acc + (m?.units?.length || 0), 0);
    const totalSessions = modules.reduce(
        (acc, m) => acc + (m?.units || []).reduce((a, u) => a + (u?.lessons?.length || 0), 0),
        0
    );
    const learnersText = Array.isArray(programAudience)
        ? programAudience.map((v) => TARGET_AGE_MAP[v]?.label || v).join(", ")
        : TARGET_AGE_MAP[programAudience]?.label || programAudience || "Not specified";


    return (
        <Document>
            <Page style={styles.page} wrap>
                {/* ===== Fixed Header ===== */}
                <View style={styles.header} fixed>
                    {PoweredByDQ && (
                        <Image style={styles.headerLogo} src={PoweredByDQ} />
                    )}
                </View>

                {/* ===== Title block ===== */}
                <Text style={styles.title}>{programName}</Text>
                <Text style={{ fontSize: 16, textAlign: "center", marginTop: 8, marginBottom:'18px', fontFamily: "Helvetica-Bold"}}>
                    Educator Teaching Plan
                </Text>

                {/* Meta */}
                <View style={styles.metaRow}>
                    <Text style={styles.metaLine}><Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Learners: </Text>{learnersText}</Text>
                    <Text style={styles.metaLine}>
                        <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Program Structure: </Text>
                        {totalModules} Module(s) / {totalUnits} Unit(s) / {totalSessions} Session(s)
                    </Text>
                    <Text style={styles.metaLine}>
                        <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Program Length: </Text>{formatDuration(programDuration)}
                    </Text>
                </View>

                {/* Overview */}
                {programDescription && (
                    <>
                        <Text style={styles.h2}>Overview</Text>
                        <Text style={{fontSize: 12 }}>{programDescription}</Text>
                    </>
                )}

                {/* Preparing Your Class */}
                <Text style={styles.h2}>Preparing Your Class</Text>
                <Text style={{fontSize: 12, marginBottom: 4}}>Before beginning the program:</Text>
                <View style={{ marginLeft: 20, marginBottom: 8 }}>
                    <View style={{ flexDirection: "row", marginBottom: 4 }}>
                        <Text style={{ marginRight: 8, fontSize: 16, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12 }}>Ensure your classroom has <Text style={{ fontFamily: "Helvetica-Bold" }}>internet access</Text> and <Text style={{ fontFamily: "Helvetica-Bold" }}>digital devices</Text> (computers or tablets).</Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 4 }}>
                        <Text style={{ marginRight: 8, fontSize: 16, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12 }}>Confirm all students have accounts and can log in to <Link src="https://www.dqforall.com" style={{ color: "#0066cc", textDecoration: "underline" }}>www.dqforall.com</Link>.</Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 4 }}>
                        <Text style={{ marginRight: 8, fontSize: 16, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12 }}>If the website is blocked, ask your <Text style={{ fontFamily: "Helvetica-Bold" }}>system administrator</Text> to whitelist or unblock it.</Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 4 }}>
                        <Text style={{ marginRight: 8, fontSize: 16, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12 }}>Encourage students to bring <Text style={{ fontFamily: "Helvetica-Bold" }}>headphones</Text> (some activities include video or audio).</Text>
                        </View>
                </View>

                {/* ===== Modules / Units / Sessions with indentation ===== */}
                {modules.map((mod, mIdx) => (
                    <View key={mIdx} style={styles.moduleBlock}>
                        <View style={styles.moduleDivider} />
                        <Text style={styles.h2}>
                            <Text>Module {mIdx + 1}: {mod.module_name} </Text>
                            <Text style={styles.durationModule}>({formatDuration(mod.module_duration)})</Text>
                        </Text>
                        {(mod.module_educator_description || mod.module_learner_description) && (
                            <Text style={{marginBottom:'10px', fontSize:'12px'}}>
                                {isEducatorView ? mod.module_educator_description : mod.module_learner_description}
                            </Text>
                        )}

                        {/* Units */}
                        {mod.units?.map((u, uIdx) => (
                            <View key={uIdx} style={styles.unitBlock}>
                                <View style={styles.unitDivider} />
                                <View style={styles.unitTitleBand}>
                                    <Text style={styles.unitTitleText}>
                                        Unit {uIdx + 1}: {u.unit_title} <Text style={styles.durationUnit}>({formatDuration(u.unit_duration)})</Text>
                                    </Text>
                                </View>
                                {(u.unit_educator_description || u.unit_learner_description) && (
                                    <Text style={{ paddingLeft: '13px', fontSize:'12px'}}>{isEducatorView ? u.unit_educator_description : u.unit_learner_description}</Text>
                                )}

                                {/* Sessions */}
                                {u.lessons?.map((s, sIdx) => {
                                    const ksabTokens = s.session_unique_ksab || [];
                                    const objectiveItems =
                                        (s.session_learning_objectives || [])
                                            .map(
                                                (o) =>
                                                    o.description_for_educator ||
                                                    o.description_for_learner ||
                                                    o.description
                                            )
                                            .filter(Boolean);

                                    const sessionDescription = isEducatorView
                                        ? s.lesson_educator_description
                                        : s.lesson_learner_description;

                                    return (
                                        <View key={sIdx} style={styles.sessionBlock}>
                                            <Text style={styles.sessionHeader}>
                                                Session {sIdx + 1}: {s.lesson_title || s.session_title || s.title || 'Untitled Session'} <Text style={styles.durationSession}>({formatDuration(s.lesson_duration || s.session_duration || s.duration || 0)})</Text>
                                            </Text>

                                            {sessionDescription ? (
                                                <Text style={styles.sessionMeta}>{sessionDescription}</Text>
                                            ) : null}

                                            {!!objectiveItems.length && (
                                                <View style={{ marginTop: 2 }}>
                                                    <Text style={{ fontSize: 13, fontFamily: "Helvetica-Bold", marginBottom: 2 }}>
                                                        Session Learning Objectives
                                                    </Text>
                                                    {objectiveItems.map((line, idx) => (
                                                        <View key={idx} style={{ flexDirection: "row",marginLeft:'20px', marginBottom: 6 }}>
                                                            <Text style={{ marginRight: 8, fontSize: 25, fontWeight: "bold", }}>•</Text>
                                                            <Text style={styles.bullet}>{line}</Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            )}
                                            {/* Estimated Time Table - 4 columns (Estimated Time, Segment, Activities, Description) */}
                                            {(() => {
                                                // Helper function to generate time table data
                                                const generateTimeTableData = () => {
                                                    const sessionDuration = s.lesson_duration || 0;
                                                    const learningObjectives = s.session_learning_objectives || [];
                                                    const hasLOs = learningObjectives.length > 0;

                                                    // Check if session has assessments (we'll assume sessions typically have them)
                                                    const hasPreAssessment = true;  // Most sessions have pre-assessment
                                                    const hasPostAssessment = true; // Most sessions have post-assessment

                                                    const segments = [];

                                                    // Calculate time percentages based on number of segments
                                                    const numSLOs = learningObjectives.length;
                                                    const hasAssessments = hasPreAssessment || hasPostAssessment;
                                                    const assessmentCount = (hasPreAssessment ? 1 : 0) + (hasPostAssessment ? 1 : 0);

                                                    // Time allocation strategy
                                                    let introPercentage, sloPercentage, reflectionPercentage, recapPercentage, assessmentPercentage;

                                                    if (hasAssessments && hasLOs) {
                                                        // Full session with assessments and SLOs
                                                        assessmentPercentage = 0.10; // 10% per assessment
                                                        introPercentage = 0.08;
                                                        const remainingPercentage = 1 - (assessmentCount * assessmentPercentage) - introPercentage - 0.12 - 0.10; // subtract intro, reflection, recap
                                                        sloPercentage = remainingPercentage / numSLOs;
                                                        reflectionPercentage = 0.12;
                                                        recapPercentage = 0.10;
                                                    } else if (hasLOs) {
                                                        // Session with SLOs but no assessments
                                                        introPercentage = 0.10;
                                                        sloPercentage = 0.60 / numSLOs;
                                                        reflectionPercentage = 0.15;
                                                        recapPercentage = 0.15;
                                                    } else {
                                                        // Simple session without SLOs
                                                        introPercentage = 0.15;
                                                        reflectionPercentage = 0.20;
                                                        recapPercentage = 0.15;
                                                    }

                                                    // Helper to check if a segment/SLO has polls
                                                    const hasPoll = (lo) => {
                                                        // Check various poll indicators
                                                        return lo?.has_poll ||
                                                               lo?.includes_poll ||
                                                               lo?.activities?.some(a => a?.type === 'poll' || a?.activity_type === 'poll') ||
                                                               false;
                                                    };

                                                    // 1. Introduction
                                                    const introActivities = ['View content slides', 'Watch introduction video'];
                                                    segments.push({
                                                        name: 'Introduction',
                                                        percentage: introPercentage,
                                                        activities: introActivities,
                                                        description: 'Students are introduced to the session topic and learning objectives.'
                                                    });

                                                    // 2. Pre-Assessment (if exists)
                                                    if (hasPreAssessment) {
                                                        segments.push({
                                                            name: 'Pre-Assessment',
                                                            percentage: assessmentPercentage,
                                                            activities: ['Complete assessment questions', 'Self-evaluation'],
                                                            description: 'Students complete a pre-assessment to gauge their existing knowledge on the topic.'
                                                        });
                                                    }

                                                    // 3. SLO Segments (Learning Content)
                                                    if (hasLOs) {
                                                        learningObjectives.forEach((lo, idx) => {
                                                            const sloTitle = lo.slo_title || `Learning Objective ${idx + 1}`;
                                                            const loDesc = lo.description_for_educator || lo.description_for_learner || '';

                                                            // Build activities list for this SLO
                                                            const sloActivities = ['Interactive activities', 'Guided practice'];
                                                            if (hasPoll(lo)) {
                                                                sloActivities.push('Answer polls');
                                                            }
                                                            sloActivities.push('Apply concepts');

                                                            segments.push({
                                                                name: sloTitle,
                                                                percentage: sloPercentage,
                                                                activities: sloActivities,
                                                                description: loDesc || 'Students engage with learning content through interactive activities.'
                                                            });
                                                        });
                                                    } else {
                                                        // If no SLOs, add a generic main content segment
                                                        segments.push({
                                                            name: 'Main Content',
                                                            percentage: 0.50,
                                                            activities: ['Interactive learning activities', 'Guided exploration', 'Group discussions'],
                                                            description: 'Students engage with the core learning content through interactive activities and discussions.'
                                                        });
                                                    }

                                                    // 4. Reflection
                                                    segments.push({
                                                        name: 'Reflection',
                                                        percentage: reflectionPercentage,
                                                        activities: ['Personal reflection', 'Answer reflection questions', 'Share insights'],
                                                        description: 'Students reflect on their learning and share key takeaways with peers.'
                                                    });

                                                    // 5. Post-Assessment (if exists)
                                                    if (hasPostAssessment) {
                                                        segments.push({
                                                            name: 'Post-Assessment',
                                                            percentage: assessmentPercentage,
                                                            activities: ['Complete assessment questions', 'Review answers'],
                                                            description: 'Students complete a post-assessment to demonstrate their understanding of the concepts learned.'
                                                        });
                                                    }

                                                    // 6. Recap
                                                    segments.push({
                                                        name: 'Recap',
                                                        percentage: recapPercentage,
                                                        activities: ['Review key concepts', 'Q&A session', 'Summary'],
                                                        description: 'Key learning points are reviewed and reinforced to ensure understanding.'
                                                    });

                                                    return segments;
                                                };

                                                const timeTableRows = generateTimeTableData();
                                                const sessionDuration = s.lesson_duration || 0;

                                                // Calculate actual minutes ensuring total equals session duration
                                                const timeAllocations = timeTableRows.map(row => Math.round(sessionDuration * row.percentage));
                                                let totalAllocated = timeAllocations.reduce((sum, time) => sum + time, 0);

                                                // Adjust to match exact session duration (handle rounding differences)
                                                if (totalAllocated !== sessionDuration && sessionDuration > 0) {
                                                    const difference = sessionDuration - totalAllocated;
                                                    // Add/subtract the difference to the largest segment (usually SLO segments in the middle)
                                                    const largestIdx = timeAllocations.indexOf(Math.max(...timeAllocations));
                                                    timeAllocations[largestIdx] += difference;
                                                }

                                                return (
                                                    <View style={{ marginTop: 12, marginBottom: 8 }}>
                                                        {/* Header Row */}
                                                        <View style={{ flexDirection: "row", borderWidth: 1, borderColor: "#000", backgroundColor: "#f0f0f0" }} wrap={false}>
                                                            <View style={{ width: "20%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                <Text style={{ fontSize: 10, fontFamily: "Helvetica-Bold" }}>Estimated Time</Text>
                                                            </View>
                                                            <View style={{ width: "20%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                <Text style={{ fontSize: 10, fontFamily: "Helvetica-Bold" }}>Segment</Text>
                                                            </View>
                                                            <View style={{ width: "25%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                <Text style={{ fontSize: 10, fontFamily: "Helvetica-Bold" }}>Activities</Text>
                                                            </View>
                                                            <View style={{ width: "35%", padding: 6 }}>
                                                                <Text style={{ fontSize: 10, fontFamily: "Helvetica-Bold" }}>Description</Text>
                                                            </View>
                                                        </View>
                                                        {/* Data Rows */}
                                                        {timeTableRows.map((row, rowIdx) => {
                                                            const estimatedMinutes = timeAllocations[rowIdx];
                                                            const timeDisplay = estimatedMinutes > 0 ? `${estimatedMinutes} min` : '-';

                                                            return (
                                                                <View key={rowIdx} style={{ flexDirection: "row", borderWidth: 1, borderColor: "#000", borderTopWidth: 0 }} wrap={false}>
                                                                    {/* Estimated Time */}
                                                                    <View style={{ width: "20%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                        <Text style={{ fontSize: 9 }}>{timeDisplay}</Text>
                                                                    </View>
                                                                    {/* Segment */}
                                                                    <View style={{ width: "20%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                        <Text style={{ fontSize: 9 }}>{row.name}</Text>
                                                                    </View>
                                                                    {/* Activities */}
                                                                    <View style={{ width: "25%", padding: 6, borderRightWidth: 1, borderColor: "#000" }}>
                                                                        {row.activities.map((activity, actIdx) => (
                                                                            <Text key={actIdx} style={{ fontSize: 9, marginBottom: 2 }}>• {activity}</Text>
                                                                        ))}
                                                                    </View>
                                                                    {/* Description */}
                                                                    <View style={{ width: "35%", padding: 6 }}>
                                                                        <Text style={{ fontSize: 9 }}>{row.description}</Text>
                                                                    </View>
                                                                </View>
                                                            );
                                                        })}
                                                    </View>
                                                );
                                            })()}

                                        </View>
                                    );
                                })}

                            </View>
                        ))}
                    </View>
                ))}
                
                <View>
                    {/* Teacher Notes - separate heading with bullet points */}
                    <Text style={styles.h2}>Teacher Notes</Text>
                    <View style={{ flexDirection: "row", marginBottom: 5 }}>
                        <Text style={{ marginRight: 8, fontSize: 14, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12, lineHeight: 1.4 }}>
                            Keep an open, curious classroom environment.
                        </Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 5 }}>
                        <Text style={{ marginRight: 8, fontSize: 14, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12, lineHeight: 1.4 }}>
                            Reinforce lessons throughout by checking in with students after each session recap using live group discussions or quick reflection activities.
                        </Text>
                    </View>
                    <View style={{ flexDirection: "row", marginBottom: 0 }}>
                        <Text style={{ marginRight: 8, fontSize: 14, fontWeight: "bold" }}>•</Text>
                        <Text style={{ fontSize: 12, lineHeight: 1.4 }}>
                            Track progress and impact via your <Text style={{ fontFamily: "Helvetica-Bold" }}>Powered by DQ School Dashboard</Text>.
                        </Text>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <View style={styles.footerRow}>
                        <CircleLetterIcon letter="C" />
                        <Text style={styles.footerCopy}>
                            {new Date().getFullYear()} DQ LAB Pte Ltd. All rights reserved. Powered by DQ
                        </Text>
                        <CircleLetterIcon letter="R" />
                        <View style={styles.footerSpacer} />
                        <Text style={styles.footerCopy}>is a registered trademark of DQ LAB Pte Ltd.</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default TeachingPlanPdfDoc;
